import {
  ArrowRightIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileIcon,
  Loader2Icon,
  Minimize2Icon,
  PictureInPictureIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  closeFullscreen,
  selectFullscreenDoc,
  unpinDocument,
} from '@/services/slices/pinnedDocsSlice';

export const FullscreenModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fullscreenDoc = useSelector(selectFullscreenDoc);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset loading state when doc changes
  useEffect(() => {
    if (fullscreenDoc) {
      setIsLoading(true);
      setError(null);
    }
  }, [fullscreenDoc?.id]);

  const handleClose = useCallback(() => {
    dispatch(closeFullscreen());
  }, [dispatch]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenDoc) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenDoc, handleClose]);

  if (!fullscreenDoc) {
    return null;
  }

  const handleUnpin = () => {
    dispatch(unpinDocument(fullscreenDoc.id));
  };

  const handleGoToDocument = () => {
    // Navigate to the matter's documents tab with blob_id param for deep linking
    // The BlobFocusHandler in DocumentExplorer will expand parent folders and select the file
    navigate(
      `/matters/${fullscreenDoc.matterId}/documents?blob_id=${fullscreenDoc.id}`,
    );
    dispatch(closeFullscreen());
    toast.success(`Navigated to ${fullscreenDoc.matterName}`);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = fullscreenDoc.downloadUrl;
    a.download = fullscreenDoc.fileName;
    a.click();
  };

  const handleOpenInNewTab = () => {
    window.open(fullscreenDoc.downloadUrl, '_blank');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const mimeType = fullscreenDoc.fileType || 'application/octet-stream';
  const isPreviewable =
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.includes('text/');

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Document preview: ${fullscreenDoc.fileName}`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      onKeyDown={(e) => {
        if (e.key === 'Escape') handleClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        style={{
          width: '90vw',
          height: '90vh',
          maxWidth: '1600px',
          maxHeight: '1000px',
        }}
      >
        {/* Header - compact */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-zinc-50 shrink-0">
          <div className="flex-1 min-w-0 mr-3">
            <h2
              className="text-sm font-semibold text-zinc-900 truncate"
              title={fullscreenDoc.fileName}
            >
              {fullscreenDoc.fileName}
            </h2>
            <p
              className="text-xs text-zinc-500 truncate"
              title={fullscreenDoc.matterName}
            >
              {fullscreenDoc.matterName}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleGoToDocument}
              className="h-6 text-[11px] px-2 text-violet-600 hover:text-violet-700 border-violet-200 hover:border-violet-300 hover:bg-violet-50"
            >
              Go to Document
              <ArrowRightIcon size={10} />
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-700"
                >
                  <DownloadIcon size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Download
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenInNewTab}
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-700"
                >
                  <ExternalLinkIcon size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Open in new tab
              </TooltipContent>
            </Tooltip>

            <div className="w-px h-4 bg-zinc-200 mx-0.5" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-700"
                >
                  <Minimize2Icon size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Exit fullscreen
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUnpin}
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-red-600"
                >
                  <PictureInPictureIcon size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Put it Back
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative bg-zinc-100 overflow-hidden">
          {!isPreviewable ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 p-8">
              <div className="bg-white rounded-full p-6 shadow-sm">
                <FileIcon className="h-16 w-16 text-zinc-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-medium text-zinc-700 mb-2">
                  Preview not available
                </p>
                <p className="text-sm text-zinc-500 mb-6">
                  This file type cannot be previewed in the browser
                </p>
                <div className="flex items-center gap-3 justify-center">
                  <Button
                    variant="default"
                    size="default"
                    onClick={handleDownload}
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    Download File
                  </Button>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={handleOpenInNewTab}
                  >
                    <ExternalLinkIcon className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Loading state */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-100 z-10">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2Icon className="h-8 w-8 animate-spin text-violet-600" />
                    <span className="text-sm text-zinc-500">
                      Loading preview...
                    </span>
                  </div>
                </div>
              )}

              {/* Error state */}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-zinc-100 z-10">
                  <div className="text-center p-6">
                    <p className="text-base text-red-600 mb-4">{error}</p>
                    <Button
                      variant="outline"
                      size="default"
                      onClick={handleOpenInNewTab}
                    >
                      <ExternalLinkIcon className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </Button>
                  </div>
                </div>
              )}

              {/* Preview content */}
              {mimeType.startsWith('image/') ? (
                <img
                  src={fullscreenDoc.downloadUrl}
                  alt={fullscreenDoc.fileName}
                  className={cn(
                    'absolute inset-0 w-full h-full object-contain transition-opacity duration-300',
                    isLoading ? 'opacity-0' : 'opacity-100',
                  )}
                  onLoad={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setError('Failed to load image');
                  }}
                />
              ) : (
                <iframe
                  src={fullscreenDoc.downloadUrl}
                  title={fullscreenDoc.fileName}
                  className={cn(
                    'absolute inset-0 w-full h-full transition-opacity duration-300',
                    isLoading ? 'opacity-0' : 'opacity-100',
                  )}
                  allow="fullscreen"
                  onLoad={() => setIsLoading(false)}
                  onError={() => {
                    setIsLoading(false);
                    setError('Failed to load preview');
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullscreenModal;
